<template>
  <div
    class="flex min-h-screen flex-col text-primary antialiased anchor-scrolling-none"
  >
    <SFSkipLinks v-model:is-mobile-sidebar-open="isMobileSidebarOpen" />
    <ClientOnly>
      <SFToastContainer />
    </ClientOnly>
    <CountryDetection @switch-shop="switchShop" />

    <div class="relative flex min-h-screen flex-col">
      <div class="sticky xs:top-0 xs:z-50 lg:static">
        <CMSBannerWrapper />
        <SFHeader
          v-model:is-mobile-sidebar-open="isMobileSidebarOpen"
          :reduced="reducedVersion"
        />
      </div>
      <main
        id="main-content"
        class="grow focus:shadow-none focus:outline-none xs:pt-0 lg:pt-0"
        data-testid="main-content"
        tabindex="-1"
      >
        <template v-if="!$slots.error">
          <NuxtPage />
        </template>
        <template v-else>
          <slot name="error" />
        </template>
      </main>
      <SFFooterNewsletter class="mt-8" />
      <SFFooterPromises v-if="!reducedVersion" />
      <SFFooter :reduced="reducedVersion" class="pb-4" />
    </div>
    <SFPromotionSlideIn :promotions="promotions?.entities" />
    <ClientOnly>
      <SFSearchFlyout v-if="isSearchFlyoutOpen" />
      <SizePickerSlideIn v-if="isSizePickerOpen" />
      <SizeBackInStockSlideIn v-if="isBackInStockOpen" />
      <SFCoinsFaqSlideIn v-if="isCoinsFaqOpen" />
      <SFStoreSelectModal />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, hydrateOnVisible, ref } from 'vue'
import { whenever } from '@vueuse/core'
import { useHead } from '#imports'
import { useRoute } from '#app/composables/router'
import { useNuxtApp } from '#app/nuxt'
import {
  useBasket,
  useCurrentPromotions,
  useCurrentShop,
} from '#storefront/composables'
import {
  createContext,
  USE_DEFAULT_BREAKPOINTS_KEY,
  USE_TRACKING_EVENTS_KEY,
  useCustomerDataChangeWatcher,
  useRouteHelpers,
  useTrackingEvents,
  useUserItemsTrackingWatcher,
} from '~/composables'
import CountryDetection, {
  type ShopInfo,
} from '~/components/SFCountryDetection.vue'
import { useDefaultBreakpoints } from '#storefront-ui/composables'
import { SFToastContainer } from '#storefront-ui/components'
import { ClientOnly, CMSBannerWrapper, NuxtPage } from '#components'
import SFSkipLinks from '~/components/SFSkipLinks.vue'
import SFFooterPromises from '~/components/layout/footer/SFFooterPromises.vue'
import SFHeader from '~/components/layout/headers/SFHeader.vue'
import { routeList } from '~/utils'
import { useShopSwitcher } from '~/composables/useShopSwitcher'
import { useLocalePath } from '#i18n'
import { useApplyPromotions } from '#storefront-promotions/composables/useApplyPromotions'
import SFPromotionSlideIn from '~/components/promotion/modal/SFPromotionSlideIn.vue'
import SFStoreSelectModal from '~/components/SFStoreSelectModal.vue'
import SFFooterNewsletter from '~/components/layout/footer/SFFooterNewsletter.vue'
import { useSlideIn } from '~/modules/ui/runtime/composables'
import {
  COINS_FAQ_SLIDEIN,
  SIZE_BACK_IN_STOCK_SLIDEIN,
  SIZE_SELECTOR_SLIDEIN,
} from '~/constants'
import SizeBackInStockSlideIn from '~/components/slidein/SizeBackInStockSlideIn.vue'

const SFSearchFlyout = defineAsyncComponent(
  () => import('~/components/layout/headers/SFSearchFlyout.vue'),
)

const SizePickerSlideIn = defineAsyncComponent(
  () => import('~/components/slidein/SizePickerSlideIn.vue'),
)

const SFCoinsFaqSlideIn = defineAsyncComponent(
  () => import('~/components/coins/SFCoinsFaqSlideIn.vue'),
)

const SFFooter = defineAsyncComponent({
  loader: () => import('~/components/footer/SFFooter.vue'),
  hydrate: hydrateOnVisible(),
})

const { isOpen: isSearchFlyoutOpen } = useSlideIn('SearchFlyout')
const { isOpen: isSizePickerOpen } = useSlideIn(SIZE_SELECTOR_SLIDEIN)
const { isOpen: isBackInStockOpen } = useSlideIn(SIZE_BACK_IN_STOCK_SLIDEIN)
const { isOpen: isCoinsFaqOpen } = useSlideIn(COINS_FAQ_SLIDEIN)
const { changeShop } = useShopSwitcher()

const route = useRoute()

const localePath = useLocalePath()
const { isRouteActive } = useRouteHelpers()
const isPreCheckoutPage = computed(() => isRouteActive(routeList.signin))
const isOSP = computed(() => route.path === localePath(routeList.osp))

const reducedVersion = computed(() => isPreCheckoutPage.value || isOSP.value)
// Initialize data
const { data: promotions } = useCurrentPromotions()

const isMobileSidebarOpen = ref(false)

const trackingEvents = useTrackingEvents()
useUserItemsTrackingWatcher()
useCustomerDataChangeWatcher()

createContext(USE_TRACKING_EVENTS_KEY, trackingEvents)
createContext(USE_DEFAULT_BREAKPOINTS_KEY, useDefaultBreakpoints())

trackingEvents.dataLayerInit()

const { data: _promotionData } = useCurrentPromotions()
const currentShop = useCurrentShop()

const { data: basketData } = useBasket()
const { applyPromotions } = useApplyPromotions({ basket: basketData })

// Update promotions in case a user with items in the basket returned to the shop.
// While the user was gone, new promotions might have been added that could be added to the basket items.
whenever(
  basketData,
  () => {
    applyPromotions()
  },
  { once: true },
)

const {
  $config: {
    public: { shopName },
  },
} = useNuxtApp()

// Meta tags
useHead({
  bodyAttrs: {
    class: ['relative'],
  },
  htmlAttrs: {
    lang: () => new Intl.Locale(currentShop.value.locale).language,
  },
  script: [
    {
      // Add loaded class to body after DOMContentLoaded
      innerHTML: `document.addEventListener('DOMContentLoaded', () => { document.body.classList.add('loaded'); });`,
    },
    // TRBO tracking script
    ...(currentShop?.value.trbo
      ? [
          {
            id: 'trbo-script',
            defer: false,
            async: true,
            src: currentShop?.value.trbo,
          },
        ]
      : []),
  ],
  titleTemplate: (title) => (title ? `${title} | ${shopName}` : `${shopName}`),
})

const switchShop = (shop: ShopInfo) => {
  changeShop(shop.path, shop.locale)
}
defineOptions({ name: 'AppDefault' })
</script>
